import { Controller } from "@hotwired/stimulus"

// Evaluates inline scripts within turbo frames after navigation
// This is necessary because Turbo Frames don't execute <script> tags by default
//
// The key challenge: Alpine's MutationObserver automatically initializes x-data
// elements as soon as they enter the DOM. We need to:
// 1. Pause Alpine's mutations before Turbo renders new content
// 2. Evaluate inline scripts (which define the component functions)
// 3. Resume Alpine so it can now find the defined functions
//
// Note: The proper long-term solution is to register components globally using
// Alpine.data() in tailadmin/index.js. This controller provides compatibility
// for inline scripts during migration.
export default class extends Controller {
  connect() {
    // Handle BEFORE frame render - pause Alpine mutations
    this.handleBeforeRender = this.handleBeforeRenderEvent.bind(this)
    document.addEventListener("turbo:before-frame-render", this.handleBeforeRender)

    // Handle AFTER frame load - evaluate scripts and resume Alpine
    this.handleFrameLoad = this.handleFrameLoadEvent.bind(this)
    this.element.addEventListener("turbo:frame-load", this.handleFrameLoad)

    // Handle browser back/forward navigation
    this.handleRender = this.handleRenderEvent.bind(this)
    document.addEventListener("turbo:render", this.handleRender)

    // Evaluate any scripts already present on initial load
    this.evaluateScripts(this.element)
  }

  disconnect() {
    document.removeEventListener("turbo:before-frame-render", this.handleBeforeRender)
    this.element.removeEventListener("turbo:frame-load", this.handleFrameLoad)
    document.removeEventListener("turbo:render", this.handleRender)
  }

  handleBeforeRenderEvent(event) {
    // Only handle if this frame is the target
    if (event.target !== this.element) return

    // Pause Alpine's mutation observer so it doesn't try to initialize
    // x-data elements before their defining scripts are evaluated
    if (typeof Alpine !== 'undefined' && Alpine.deferMutations) {
      Alpine.deferMutations()
    }
  }

  handleFrameLoadEvent(event) {
    // First, evaluate all inline scripts (which define the component functions)
    this.evaluateScripts(this.element)

    // Now resume Alpine - it will process the x-data elements and find the functions
    if (typeof Alpine !== 'undefined') {
      if (Alpine.flushAndStopDeferringMutations) {
        Alpine.flushAndStopDeferringMutations()
      }

      // Manually initialize any uninitialized Alpine elements
      this.initializeAlpine(this.element)
    }
  }

  handleRenderEvent(event) {
    // This fires after Turbo renders content (including from cache on back/forward)
    // Re-evaluate scripts and reinitialize Alpine
    this.evaluateScripts(this.element, { force: true })
    this.initializeAlpine(this.element)
  }

  evaluateScripts(container, options = {}) {
    const force = options.force || false

    // Find all script tags within the container (skip those marked to not evaluate)
    const scripts = container.querySelectorAll('script:not([data-turbo-eval="false"])')

    scripts.forEach(script => {
      // Skip if already evaluated (unless forcing re-evaluation)
      if (!force && script.dataset.evaluated === 'true') return

      // Mark as evaluated to prevent re-running
      script.dataset.evaluated = 'true'

      // Create a new script element and copy content
      const newScript = document.createElement('script')

      // Copy attributes
      Array.from(script.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value)
      })

      // Copy content
      newScript.textContent = script.textContent

      // Execute synchronously by appending to document head
      document.head.appendChild(newScript)
      document.head.removeChild(newScript)
    })
  }

  initializeAlpine(container) {
    if (typeof Alpine === 'undefined') return

    // Find uninitialized Alpine elements and initialize them
    // Alpine marks initialized elements with _x_dataStack (Alpine v3)
    container.querySelectorAll('[x-data]').forEach(el => {
      if (!el._x_dataStack) {
        Alpine.initTree(el)
      }
    })
  }
}
